import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, BadgeCheck, BookOpenText, CreditCard, Megaphone, Search, UserRound } from "lucide-react";
import type { NavItem } from "../App";
import type { ManualEntry } from "../guide/manualIndex";

interface Cmd {
  id: string;
  label: string;
  meta?: string;
  section: string;
  icon: NavItem["icon"];
  go: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Cmd/Ctrl+K: jump to any page you can see, or look up a player or order. */
export function CommandPalette({ open, onClose, nav }: { open: boolean; onClose: () => void; nav: { group: string; items: NavItem[] }[] }) {
  const ref = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const navigate = useNavigate();
  // The Operator Manual's sections (loaded the first time the palette opens): typing a section's title or a topic it covers jumps there.
  const [manual, setManual] = useState<ManualEntry[]>([]);
  useEffect(() => {
    if (open && !manual.length) void import("../guide/manualIndex").then((m) => setManual(m.MANUAL_INDEX)).catch(() => undefined);
  }, [open, manual.length]);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      setQ("");
      setSel(0);
      d.showModal();
      input.current?.focus();
    } else if (!open && d.open) d.close();
  }, [open]);

  const items = useMemo<Cmd[]>(() => {
    const t = q.trim();
    const words = t.toLowerCase().split(/\s+/).filter(Boolean);
    const match = (hay: string) => words.every((w) => hay.toLowerCase().includes(w));
    const pages: Cmd[] = nav.flatMap((g) =>
      g.items.flatMap((i) => [
        ...(match(i.label + " " + g.group + " " + (i.hint ?? "")) ? [{ id: "p:" + i.to, label: i.label, meta: g.group, section: "Pages", icon: i.icon, go: i.to }] : []),
        // A page's sections (e.g. Ads: partners, rate cards) show once you type.
        ...(t
          ? (i.subs ?? [])
              .filter((x) => match(x.label + " " + i.label + " " + (x.hint ?? "") + " " + (i.hint ?? "")))
              .map((x) => ({ id: "s:" + x.to, label: x.label, meta: i.label, section: "Pages", icon: i.icon, go: x.to }))
          : []),
      ]),
    );
    // Pages whose name matches come before those matched by group or hint words.
    if (t) pages.sort((a, b) => Number(!match(a.label)) - Number(!match(b.label)));
    const ads = nav.some((g) => g.items.some((i) => i.to === "/ads"));
    const finds: Cmd[] = [];
    if (t) {
      if (UUID.test(t)) {
        finds.push({ id: "pid", label: "Open player " + t.slice(0, 8) + "…", section: "Look up", icon: UserRound, go: "/players/" + t });
        finds.push({ id: "oid", label: "Open order " + t.slice(0, 8) + "…", section: "Look up", icon: CreditCard, go: "/finance/orders/" + t });
        if (ads) {
          finds.push({ id: "cid", label: "Open ad campaign " + t.slice(0, 8) + "…", section: "Look up", icon: Megaphone, go: "/ads/campaigns/" + t });
          finds.push({ id: "aid", label: "Open ad partner " + t.slice(0, 8) + "…", section: "Look up", icon: BadgeCheck, go: "/ads/partners/" + t });
        }
      }
      finds.push({ id: "find", label: `Find players matching “${t}”`, meta: "name, id, order or phone", section: "Look up", icon: Search, go: "/players?q=" + encodeURIComponent(t) });
    }
    const sections: Cmd[] = t
      ? manual
          .filter((m) => match(m.title + " manual " + m.words))
          .sort((a, b) => Number(!match(a.title)) - Number(!match(b.title)))
          .slice(0, 6)
          .map((m) => ({ id: "m:" + m.id, label: m.title, meta: "Operator manual", section: "Manual", icon: BookOpenText, go: "/manual#" + m.id }))
      : [];
    return [...finds, ...pages, ...sections];
  }, [q, nav, manual]);

  useEffect(() => setSel(0), [q]);

  const run = (c?: Cmd) => {
    if (!c) return;
    onClose();
    navigate(c.go);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel((s) => Math.min(items.length - 1, s + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel((s) => Math.max(0, s - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(items[sel]);
    }
  };
  useEffect(() => {
    ref.current?.querySelector(`[data-i="${sel}"]`)?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  let last = "";
  return (
    <dialog
      ref={ref}
      className="cmdk"
      aria-label="Search the console"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
      data-testid="cmdk"
    >
      <div className="cmdk-input">
        <Search aria-hidden="true" />
        <input
          ref={input}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Search players by name or id, or jump to a page"
          aria-label="Search"
          aria-controls="cmdk-list"
          aria-activedescendant={items[sel] ? "cmdk-" + sel : undefined}
          role="combobox"
          aria-expanded="true"
        />
      </div>
      <div className="cmdk-list" id="cmdk-list" role="listbox">
        {items.length === 0 && <div className="state">No page matches “{q}”.</div>}
        {items.map((c, i) => {
          const head = c.section !== last ? <div className="cmdk-sec">{c.section}</div> : null;
          last = c.section;
          return (
            <div key={c.id}>
              {head}
              <button
                id={"cmdk-" + i}
                data-i={i}
                role="option"
                aria-selected={i === sel}
                className="cmdk-item"
                onMouseMove={() => setSel(i)}
                onClick={() => run(c)}
                tabIndex={-1}
              >
                <c.icon aria-hidden="true" />
                <span className="grow">{c.label}</span>
                {c.meta && <span className="meta">{c.meta}</span>}
                {i === sel && <ArrowRight aria-hidden="true" />}
              </button>
            </div>
          );
        })}
      </div>
      <div className="cmdk-foot">
        <span>
          <kbd>↑</kbd> <kbd>↓</kbd> move
        </span>
        <span>
          <kbd>Enter</kbd> open
        </span>
        <span>
          <kbd>Esc</kbd> close
        </span>
      </div>
    </dialog>
  );
}
